import { gql } from "@apollo/client";

export const DELIVERY_TASK_ORDER_FIELDS = gql`
  fragment DeliveryTaskOrderFields on DeliveryTaskOrderType {
    deliveryTaskId
    dspId
    id
    isAbnormal
    isDelivered
    isDspArrivalScanCompleted
    isNewAssignment
    isPickedUp
    isReturnToWarehouse
    isTransferIn
    isTransferOut
    orderId
    routeName
    stopNumber
    subStopNumber
    trackingNumber
    podUpdateTimeString
    isAllowUpdatePod
    isPopUp
    transferFromDriverName
    transferToDriverName
    updatedAt
    isDelivery
    agingLevel
    taskDate
    addressGroupKey
    deliveryTaskType {
      id
      routeHistoryId
      createdAt
    }
    orderWithAddress {
      deliveryAddress {
        addressLine1
        addressLine2
        cameraCount
        doorNumber
        doorSubNumber
        cameraConfig
        city
        company
        countryCode
        email
        extension
        fullName
        id
        instructions
        phoneNumber
        state
        zipcode
        latitude
        longitude
        isAddressChanged
        standardizedAddressString
      }
      order {
        createdAt
        deliverAt
        deliveredAt
        preDeliveredAt
        deliveryAttempts
        exceptionImageS3KeysJson
        exceptionReasonCode
        expectedDeliveryTime
        id
        isAbnormal
        isDelivered
        isFinished
        lastDeliveryScanTime
        orderTime
        overallStatus
        podImageS3KeysJson
        podReviewStatus
        podReviewMessage {
          id
          failureOtherReason
          lang
          translateFailureOtherReason {
            lang
            translateFailureOtherReason
          }
        }
        podReviewReasons {
          l1Code
          l1Label {
            zh
            en
            es
          }
          driverNotification {
            zh
            en
            es
          }
          l2Reasons {
            l2Code
            l2Label {
              zh
              en
              es
            }
            isLegacy
          }
        }
        receiverZipcode
        sortingCode
        trackingNumber
        updatedAt
        nextAction
        isReturnToWhsScanned
      }
    }
    orderNote {
      id
      note
      lang
      orderNoteTranslations {
        lang
        translateNote
      }
    }
  }
`;

export const DELIVERY_TASK_ORDER_DETAIL_FIELDS = gql`
  fragment DeliveryTaskOrderFields on DeliveryTaskOrderType {
    deliveryTaskId
    dspId
    id
    isAbnormal
    isDelivered
    orderId
    routeName
    stopNumber
    subStopNumber
    trackingNumber
    podUpdateTimeString
    isAllowUpdatePod
    isPopUp
    updatedAt
    deliveryMethod
    orderWithAddress {
      deliveryAddress {
        addressLine1
        addressLine2
        cameraCount
        doorNumber
        doorSubNumber
        cameraConfig
        city
        company
        countryCode
        email
        extension
        fullName
        id
        instructions
        phoneNumber
        state
        zipcode
        latitude
        longitude
        isCoordinatesUpdated
        standardizedAddressString
      }
      order {
        destHubArrivedAt
        destWarehouseArrivedAt
        channelId
        channelOrderNumber
        createdAt
        deliverAt
        deliveredAt
        preDeliveredAt
        deliveryAttempts
        exceptionImageS3KeysJson
        exceptionReasonCode
        expectedDeliveryTime
        hasManded
        heightCm
        id
        isAbnormal
        isDestHubArrived
        isDestWarehouseArrived
        isComplaint
        isDelivered
        isFinished
        isIntercepted
        isReceiverAddressModified
        lengthCm
        orderTime
        overallStatus
        packageValueUsd
        isDeliveryPickedUp
        podImageS3KeysJson
        podReviewStatus
        podReviewMessage {
          id
          failureOtherReason
          lang
          translateFailureOtherReason {
            lang
            translateFailureOtherReason
          }
        }
        podHasBeenManualDisapproved
        isModifiedPodPhotos
        receiverZipcode
        recipientAddressId
        securityRnd
        senderAddressId
        sortingCode
        trackingNumber
        updatedAt
        volumetricWeightKg
        weightKg
        widthCm
        exceptionImageS3UrlsJson
        podImageS3UrlsJson
        nextAction
        isHasBeenSetException
        isReturnToWhsScanned
      }
    }
    orderNote {
      id
      note
      lang
      operatorName
      orderNoteTranslations {
        lang
        translateNote
      }
    }
    returnToWarehouseReason
    deliveryRevokeStatus {
      canRevoke
      revokeDeadline
      hintMessage {
        zh
        en
        es
        appendDeadline
      }
    }
  }
`;

export const DSP_ORDER_FIELDS = gql`
  fragment DeliveryTaskOrderFields on DeliveryTaskOrderType {
    id
    orderId
    routeName
    stopNumber
    subStopNumber
    trackingNumber
  }
`;

export interface DeliveryTaskType {
  driverAccountId: number;
  id: number;
  routeHistoryId: number;
  routeName: string;
  createdAt: string;
}

export interface OrderAddressType {
  addressLine1: string;
  addressLine2: string;
  cameraCount?: number;
  doorNumber?: string;
  doorSubNumber?: string;
  cameraConfig?: string;
  city: string;
  company: string;
  countryCode: number;
  email: string;
  extension: string;
  fullName: string;
  id: number;
  instructions: string;
  phoneNumber: string;
  state: string;
  zipcode: string;
  // 地理坐标
  latitude?: number;
  longitude?: number;
  isCoordinatesUpdated: boolean;
  isAddressChanged?: boolean;
  // 标准化地址
  standardizedAddressString?: string | null;
}

export interface OrderType {
  destHubArrivedAt: string;
  destWarehouseArrivedAt: string;
  channelId: number;
  channelOrderNumber: number;
  createdAt: string;
  deliverAt: string;
  deliveredAt: string;
  preDeliveredAt?: string | null;
  deliveryAttempts: number;
  exceptionImageS3KeysJson: string[];
  exceptionReasonCode: string;
  expectedDeliveryTime: string;
  hasManded: boolean;
  heightCm: number;
  id: number;
  isAbnormal: boolean;
  isDestHubArrived: boolean;
  isDestWarehouseArrived: boolean;
  isComplaint: boolean;
  isDelivered: boolean;
  isFinished: boolean;
  isIntercepted: boolean;
  isReceiverAddressModified: boolean;
  lengthCm: number;
  lastDeliveryScanTime?: number | null;
  orderTime: string;
  overallStatus: string;
  packageValueUsd: number;  
  isDeliveryPickedUp: string;
  podImageS3KeysJson: string[];
  podReviewStatus: number;
  podReviewMessage: PodReviewMessageItemType[] | null;
  podReviewReasons?: PodReviewReasonType[] | null;
  podHasBeenManualDisapproved?: boolean;
  isModifiedPodPhotos?: boolean;
  receiverZipcode: string;
  recipientAddressId: number;
  securityRnd: number;
  senderAddressId: number;  
  sortingCode: string;
  trackingNumber: string;
  updatedAt: string;
  volumetricWeightKg: number;
  weightKg: number;
  widthCm: number;
  exceptionImageS3UrlsJson: string[];
  podImageS3UrlsJson: string[];
  nextAction: string;
  isHasBeenSetException: boolean;
  isReturnToWhsScanned: boolean;
}

export interface OrderWithAddressType {
  deliveryAddress: OrderAddressType;
  order: OrderType;
  pickupAddress: OrderAddressType;
}

export interface OrderNoteTranslationType {
  lang: string;
  translateNote: string;
}

export interface PodReviewMessageTranslationItemType {
  lang: string;
  translateFailureOtherReason: string;
}

export interface PodReviewMessageItemType {
  id?: number | null;
  failureOtherReason: string;
  lang?: string | null;
  translateFailureOtherReason?: PodReviewMessageTranslationItemType[] | null;
}

export interface PodReviewReasonLabelType {
  zh: string;
  en: string;
  es: string;
}

export interface PodReviewL2ReasonItemType {
  l2Code: string;
  l2Label: PodReviewReasonLabelType;
  isLegacy: boolean;
}

export interface PodReviewReasonType {
  l1Code: string;
  l1Label: PodReviewReasonLabelType;
  driverNotification: PodReviewReasonLabelType;
  l2Reasons: PodReviewL2ReasonItemType[];
}

export interface OrderNoteType {
  id: number;
  note: string;
  lang: string;
  operatorName?: string | null;
  orderNoteTranslations: OrderNoteTranslationType[];
}

export interface DeliveryTaskOrderType {
  deliveryTaskId: number;
  dspId: number;
  id: number;
  isAbnormal: boolean;
  isDelivered: boolean;
  isDspArrivalScanCompleted: boolean;
  isNewAssignment: boolean;
  isPickedUp: boolean;
  isReturnToWarehouse: boolean;
  isTransferIn: boolean;
  isTransferOut: boolean;
  orderId: number;
  routeName: string;
  stopNumber: number;
  subStopNumber: string;
  trackingNumber: string;
  transferFromDriverName: string;
  transferToDriverName: string;
  updatedAt: string;
  isDelivery: boolean;
  deliveryMethod?: string | null;
  deliveredDriverAccountName: string;
  agingLevel: number;
  taskDate?: number | null;
  addressGroupKey?: string | null;
  deliveryTaskType: DeliveryTaskType;
  orderWithAddress: OrderWithAddressType;
  orderNote?: OrderNoteType;
  returnToWarehouseReason: string;
  deliveryRevokeStatus?: {
    canRevoke: boolean;
    revokeDeadline: number | null;
    hintMessage?: {
      zh: string;
      en: string;
      es: string;
      appendDeadline: boolean;
    } | null;
  } | null;
  podUpdateTimeString?: string | null;
  isAllowUpdatePod?: boolean;
  isPopUp?: boolean;
}